import {red} from "@mui/material/colors"
import {createTheme} from "@mui/material/styles"
import {alpha} from "@mui/material/styles"

const theme = createTheme({
    palette: {
        gradient: {
            main: "#222851",
        },
        icon: {
            main: "#E3A107",
            disabled: "#FFFFFF99",
            error: "#E01D1D",
            white: "#FFFFFF",
            white70: "#FFFFFFB2",
            white60: "#FFFFFF99",
        },
        ellipse: {
            primary: "#E41953",
        },
        background: {
            default: "#f2f2f2",
            paper: "#272D42",
            primary: "#14192B",
            secondary: "#282c41",
            tertiary: "#2f3347",
            chart: "#32374b"
        },
        primary: {
            main: "#222851",
            inverse: "rgba(255, 255, 255, 1)",
            contrastText: "#fff",
            main2: "#E3A404",
            main3: "#E3A107",
            white70: "#FFFFFFB2",
        },
        secondary: {
            main: "#19857b",
            contrastText: "#fff",
            tertiary: "#FFFFFF99",
        },
        white: {
            main: "#FFFFFF",
        },
        warning: {
            main: "#D29F09",
        },
        error: {
            main: red.A400,
            light: "#FF6B6B",
            contrastText: "#fff",
        },
        success: {
            main: "#2BCE95",
            contrastText: "#fff",
        },
        info: {
            main: "#24346C",
            contrastText: "#fff",
        },
        gray: {
            main: "#B2B2B2",
            secondary: "#E2E1E1",
            contrastText: "#fff",
        },
        grey: {
            500: "#B2B2B2",
            300: "#E2E1E1",
        },
        text: {
            primary: "#14192B",
            secondary: "#B2B2B2",
            disabled: "#FFFFFF99",
            warning: "#E01D1D",
        },
        divider: "#E2E1E1",
        snackBar: {
            primary: "#3E8F6C",
            error: "#E01D1D",
            warning: "#E3A107"
        },
        input: {
            dynamicTextarea: {
                background: {
                    default: "#32384c",
                    disabled: "#24293c",
                    hover: "#54596a",
                },
                border: {
                    default: "#a0a6b5",
                    error: "#f87171",
                    hover: "#c5cad9",
                    focus: "#b9becc",
                },
                text: {
                    default: "#ffffff",
                    disabled: "#9aa0ae",
                },
            },
            placeholder: {
                primary: "#FFFFFF99",
            },
            background: {
                primary: "#202536",
                secondary: "#FFFFFF4D",
                white05: "#FFFFFF0D",
                white30: "#FFFFFF4D"
            },
            borderColor: {
                primary: "#FFFFFFB2",
                warning: "#E01D1D",
                white: "#FFFFFF",
                white70: "#FFFFFFB2",
            },
            label: {
                primary: "#c1c3c9",
                secondary: "#d1d3d7",
            },
        },
        common: {
            black: "#000",
            white: "#FFFFFF",
            gray: "#BABABA",
        },
        transparent: {
            white10: "#ffffff1a",
        },
    },
    customGradients: {
        primary: "linear-gradient(to right, #222851, #6a7dfe)",
    },
    typography: {
        fontFamily: "Titillium Web, sans-serif",
        fontSize: 14,
        fontSizes: {
            base: 16,
            xs: 10,
            sm: 14,
            md: 18,
            lg: 24,
            xl: 36,
            xxl: 64,
        },
        fontWeights: {
            light: 300,
            regular: 400,
            medium: 500,
            bold: 700,
        },
        h1: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 300,
            fontSize: "4rem",
            lineHeight: "100%",
            letterSpacing: "0%",
        },
        h2: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 300,
            fontSize: "2.25rem",
            lineHeight: "100%",
            letterSpacing: "0%",
        },
        h3: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 400,
            fontSize: "1.5rem",
            lineHeight: "100%",
            letterSpacing: "0%",
        },
        body1: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 300,
            fontSize: "1.125rem",
            lineHeight: "100%",
            letterSpacing: "0%",
        },
        body2: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 400,
            fontSize: "0.875rem",
            lineHeight: "100%",
            letterSpacing: "0%",
        },
        textLarge: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 300,
            fontSize: "1.125rem",
            lineHeight: "100%",
            letterSpacing: "0%",
        },
        textBody: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 400,
            fontSize: "0.875rem",
            lineHeight: "150% !important",
            letterSpacing: "0%",
        },
        contraHeading: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 700,
            fontSize: "0.625rem",
            lineHeight: "100%",
            letterSpacing: "0%",
            textTransform: "uppercase",
        },
        captionInfo: {
            fontFamily: "Titillium Web, sans-serif",
            fontWeight: 400,
            fontSize: "0.75rem",
            lineHeight: "150%",
            letterSpacing: "0%",
        },
    },
    breakpoints: {
        values: {
            xxxs: 0,
            xs: 375,
            sm: 390,
            md: 834,
            lg: 1100,
            xlg: 1440,
            xl: 1920,
        },
    },
    components: {
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    backgroundColor: "#1E2A47",
                    border: "1px solid #444F73",
                    color: "#B2B2B2",
                    "& .MuiAutocomplete-option:hover": {
                        backgroundColor: "#FFFFFF4D",
                    },
                    "& .MuiAutocomplete-listbox": {
                        maxHeight: 200,
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                            width: "16px",
                            background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#D9D9D9",
                            opacity: "40%",
                            borderRadius: "8px",
                            border: `5px solid transparent`,
                            backgroundClip: "padding-box",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                    }
                },
            },
        },
        MuiGrid: {
            defaultProps: {
                columns: {xs: 4, md: 12, lg: 12},
            },
        },
        MuiTypography: {
            styleOverrides: {
                h1: {
                    fontFamily: "Titillium Web, sans-serif !important",
                    fontWeight: "300 !important",
                    fontSize: "4rem !important",
                    lineHeight: "100% !important",
                    letterSpacing: "0% !important",
                },
                h2: {
                    fontFamily: "Titillium Web, sans-serif !important",
                    fontWeight: "300 !important",
                    fontSize: "2.25rem !important",
                    lineHeight: "100% !important",
                    letterSpacing: "0% !important",
                },
                h3: {
                    fontFamily: "Titillium Web, sans-serif !important",
                    fontWeight: "400 !important",
                    fontSize: "1.5rem !important",
                    lineHeight: "100% !important",
                    letterSpacing: "0% !important",
                },
                body1: {
                    fontFamily: "Titillium Web, sans-serif !important",
                    fontWeight: "300 !important",
                    fontSize: "1.125rem !important",
                    lineHeight: "100% !important",
                    letterSpacing: "0% !important",
                },
                body2: {
                    fontFamily: "Titillium Web, sans-serif !important",
                    fontWeight: "400 !important",
                    fontSize: "0.875 !important",
                    lineHeight: "100% !important",
                    letterSpacing: "0% !important",
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        margin: 0,
                        padding: 0,
                        backgroundColor: "#fff",
                        color: "#000",
                        fontFamily:
                            '"Roboto", "Helvetica", "Arial", sans-serif',
                    },
                },
            },
            variants: [
                {
                    props: {variant: "textLarge"},
                    style: {
                        fontFamily: "Titillium Web, sans-serif",
                        fontWeight: 300,
                        fontSize: "1.125rem",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                    },
                },
                {
                    props: {variant: "textBody"},
                    style: {
                        fontFamily: "Titillium Web, sans-serif",
                        fontWeight: 400,
                        fontSize: "0.875",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                    },
                },
                {
                    props: {variant: "contraHeading"},
                    style: {
                        fontFamily: "Titillium Web, sans-serif",
                        fontWeight: 700,
                        fontSize: "0.625",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        textTransform: "uppercase",
                    },
                },
                {
                    props: { variant: "captionInfo" },
                    style: {
                        fontFamily: "Titillium Web, sans-serif",
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        lineHeight: "150%",
                        letterSpacing: "0%",
                    },
                },
            ],
        },
        MuiPickersPopper: {
            styleOverrides: {
                paper: ({theme}) => ({
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.input.borderColor.primary}`,
                }),
            },
        },
        MuiPickersYear: {
            styleOverrides: {
                yearButton: ({theme}) => ({
                    color: theme.palette.common.white,
                    borderRadius: 8,
                    transition: "background-color 120ms ease-in-out",

                    "&:hover": {
                        backgroundColor: alpha("#000", 0.2), // escurece no hover
                    },

                    "&.Mui-selected": {
                        backgroundColor: alpha(theme.palette.primary.main2, 0.15),
                        border: `1px solid ${theme.palette.primary.main2}`,
                        color: theme.palette.common.white,

                        "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main2, 0.25),
                        },
                    },
                }),
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: ({theme}) => ({
                    color: theme.palette.common.white,
                    borderRadius: 6,

                    "&:hover": {
                        backgroundColor: alpha(theme.palette.common.white, 0.08),
                    },

                    "&.Mui-selected": {
                        backgroundColor: alpha(theme.palette.primary.main2, 0.15),
                        border: `1px solid ${theme.palette.primary.main2}`,
                        color: theme.palette.common.white,

                        "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main2, 0.25),
                        },
                    },

                    "&.MuiPickersDay-today": {
                        border: `1px solid ${theme.palette.primary.main2}`,
                        backgroundColor: alpha(theme.palette.primary.main2, 1),
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: ({theme}) => ({
                    color: theme.palette.common.white,
                }),
                switchViewButton: ({theme}) => ({
                    color: theme.palette.icon.main,
                }),
                label: ({theme}) => ({
                    color: theme.palette.common.white,
                    fontWeight: 600,
                }),
            },
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: ({theme}) => ({
                    color: theme.palette.text.secondary,
                }),
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({theme}) => ({
                    color: theme.palette.icon.main,
                }),
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                grouped: () => ({
                    border: "1px solid rgba(255,255,255,0.35)",
                    "&:not(:first-of-type)": {
                        border: "1 !important",
                        marginLeft: 0,
                    },
                    "&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton": {
                        border: "1px solid rgba(255,255,255,0.35)",
                    },
                }),
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    textTransform: "none",
                    borderRadius: 6,
                    padding: theme.spacing(0.5, 1.5),
                    minHeight: 32,

                    // 👇 SEM BORDA no estado normal
                    border: "1px solid",
                    color: "rgba(255,255,255,0.45)",
                    backgroundColor: "rgba(255,255,255,0.04)",
                    transition: theme.transitions.create(
                        ["background-color", "color", "border"],
                        { duration: theme.transitions.duration.shortest }
                    ),
                    "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                    },
                    "&.Mui-selected": {
                        border: "1px solid rgba(255,255,255,0.35)",
                        color: "#fff",
                        backgroundColor: "rgba(255,255,255,0.18)",

                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.22)",
                        },
                    },
                }),
            },
        },

        MuiMenu: {
            defaultProps: {
                // Select menus do not need to lock the page. MUI's scroll lock
                // compensates the body width and shifts the v2 header while open.
                disableScrollLock: true,
            },
        },
        MuiButtonGroup: {
            styleOverrides: {
                root: () => ({
                    borderRadius: 20,
                    overflow: "hidden",
                    backgroundColor: "rgba(255,255,255,.1)",
                }),

                // quando o ButtonGroup está "desselecionado" (text)
                groupedText: () => ({
                    border: "0 !important",
                    "&:not(:first-of-type)": {
                        border: "0 !important",
                    },
                }),

                // quando o ButtonGroup está "selecionado" (outlined)
                groupedOutlined: () => ({
                    border: "1px solid rgba(255,255,255,0.35) !important",
                    "&:not(:first-of-type)": {
                        borderLeft: "0 !important", // evita linha dupla no meio
                    },
                }),
            },
        },
    },
    layout: {
        drawerWidth: 300,
        subMenuHeight: 48,
    },
})

export default theme
